import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import D3EFeature from "../models/D3EFeature";

type _TemplateFeatureViewOnSelect = (feature: D3EFeature) => void;

type _ImageContainerOnTap = (d3eState: TemplateFeatureViewRefs) => void;

export interface TemplateFeatureViewProps extends BaseUIProps {
  key?: string;
  obj: D3EFeature;
  isOverView: boolean;
  onSelect: _TemplateFeatureViewOnSelect;
}
/// To store state data for TemplateFeatureView
class TemplateFeatureViewRefs {
  public imageContainer: ImageContainerState = new ImageContainerState();
}

interface ImageContainerWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: TemplateFeatureViewRefs;
  _onCardTapHandler?: _ImageContainerOnTap;
  isOverView: boolean;
  obj: D3EFeature;
}

class ImageContainerState extends ObjectObservable {
  private _hover: boolean = false;
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _ImageContainerWithState extends ObservableComponent<ImageContainerWithStateProps> {
  public constructor(props: ImageContainerWithStateProps) {
    super(props);

    this.initState();
  }
  public get imageContainer(): ImageContainerState {
    return this.props.d3eState.imageContainer;
  }
  public get isOverView(): boolean {
    return this.props.isOverView;
  }
  public get obj(): D3EFeature {
    return this.props.obj;
  }
  public get d3eState(): TemplateFeatureViewRefs {
    return this.props.d3eState;
  }
  public get _onCardTapHandler(): _ImageContainerOnTap {
    return this.props._onCardTapHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("imageContainer", null, this.imageContainer);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(
      [
        "imageContainer",
        "imageContainer.hover",
        "isOverView",
        "obj",
        "obj.androidScreenshot",
        "obj.webScreenshot",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: ImageContainerWithStateProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }
  }
  public imageContainerOnEnter(event): void {
    return this.imageContainer.setHover(true);
  }
  public imageContainerOnExit(event): void {
    return this.imageContainer.setHover(false);
  }
  public dispose(): void {
    this.imageContainer.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    return ui.Container({
      clipBehavior: ui.Clip.hardEdge,
      states: ui.joinStates(
        { "data-c0": this.imageContainer.hover },
        this.props.states
      ),
      decoration: this.imageContainer.hover
        ? new ui.BoxDecoration({
            color: new ui.Color(0xffffffff),
            border: ui.Border.all({
              color: new ui.Color(0xffdcdcdc),
              width: 1.0,
            }),
            boxShadow: [
              new ui.BoxShadow({
                color: new ui.Color(0x32000000),
                blurRadius: 5,
              }),
            ],
            borderRadius: ui.BorderRadius.circular(3.0),
          })
        : new ui.BoxDecoration({}),
      child: ui.NetworkImage({
        url: this.isOverView
          ? this.obj.webScreenshot?.downloadUrl ?? "" + "?w=100&h=100"
          : this.obj.androidScreenshot?.downloadUrl ?? "" + "?w=100&h=100",
        fit: ui.BoxFit.contain,
        className: "hc vc",
      }),
      onEnter: (event) => {
        this.imageContainerOnEnter(event);
      },
      onExit: (event) => {
        this.imageContainerOnExit(event);
      },
      onTap: (e) => {
        e.stopPropagation();

        this._onCardTapHandler(this.d3eState);
      },
      className: ui.join(
        this.props.className,
        "TemplateFeatureView x8c3 hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
}
function ImageContainerWithState(props: ImageContainerWithStateProps) {
  return React.createElement(_ImageContainerWithState, props);
}

class _TemplateFeatureViewState extends ObservableComponent<TemplateFeatureViewProps> {
  static defaultProps = { obj: null, isOverView: false, onSelect: null };
  d3eState: TemplateFeatureViewRefs = new TemplateFeatureViewRefs();
  public constructor(props: TemplateFeatureViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): D3EFeature {
    return this.props.obj;
  }
  public get isOverView(): boolean {
    return this.props.isOverView;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(["isOverView", "obj"], this.rebuild);
  }
  public componentDidUpdate(prevProps: TemplateFeatureViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }

    if (prevProps.isOverView !== this.props.isOverView) {
      this.fire("isOverView", this);
    }
  }
  public render(): ReactNode {
    return ImageContainerWithState({
      d3eState: this.d3eState,
      _onCardTapHandler: this.onCardTapHandler,
      isOverView: this.isOverView,
      obj: this.obj,
      className: this.props.className,
      ...copyBaseUIProps(this.props),
    });
  }
  public onCardTapHandler = (d3eState: TemplateFeatureViewRefs): void => {
    this.onSelect(this.obj);
  };
  public get onSelect(): _TemplateFeatureViewOnSelect {
    return this.props.onSelect;
  }
  public get imageContainer() {
    return this.d3eState.imageContainer;
  }
}
export default function TemplateFeatureView(props: TemplateFeatureViewProps) {
  return React.createElement(_TemplateFeatureViewState, {
    ..._TemplateFeatureViewState.defaultProps,
    ...props,
  });
}
